import {ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, Input, OnChanges, signal, SimpleChanges,} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Color, NgxChartsModule, ScaleType} from '@swimlane/ngx-charts';
import {Risk, RISK_COLOR} from '@/app/utils/risk-colors';
import {NgxColor} from '../donut--chart-card/donut-chart.component';

export type MultiSeries = Array<{
  name: string;
  series: Array<{ name: string; value: number }>;
}>;

@Component({
  selector: 'app-bar-card',
  standalone: true,
  imports: [CommonModule, NgxChartsModule],
  templateUrl: './bar-card.component.html',
  styleUrls: ['./bar-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BarCardComponent implements OnChanges {
  constructor(private cdr: ChangeDetectorRef) {}

  // ---- Inputs de card ----
  @Input() title = 'Distribución';
  @Input() totalHint?: string;

  // Tamaño de vista (si lo usas para contenedor externo)
  @Input() view: [number, number] = [430, 300];

  // Ejes / leyenda
  @Input() showXAxis = true;
  @Input() showYAxis = true;
  @Input() showLegend = false;
  @Input() showXAxisLabel = true;
  @Input() showYAxisLabel = false;
  @Input() xAxisLabel = 'Número de encuestados';
  @Input() yAxisLabel = '';

  // Visual
  @Input() gradient = false;
  @Input() animations = false;

  // Leyenda externa (si la usas)
  @Input() legendData: { name: string; value: any }[] = [];

  // Colores personalizados (opcional)
  @Input() customColors: NgxColor[] = [];

  colorScheme: string | Color = 'vivid';
  computedView: [number, number] = this.view;
  xTicks: number[] = [0, 1];
  readonly normalized = computed(() => {
    const raw = this._raw();
    if (!raw || raw.length === 0) return [];
    
    // Agregar identificadores únicos para evitar que se resalten múltiples barras con el mismo valor
    return raw.map((group, groupIndex) => ({
      ...group,
      series: group.series.map((item, itemIndex) => ({
        ...item,
        // Crear un identificador único combinando el nombre del grupo, item y valor
        name: `${group.name}_${item.name}_${groupIndex}_${itemIndex}`,
        // Mantener el nombre original para mostrar en la leyenda/tooltip
        originalName: item.name,
        originalGroupName: group.name
      }))
    }));
  });
  private readonly _raw = signal<MultiSeries>([]);

  // Setter para resultados (se recalculan colores y ticks)
  @Input() set results(value: MultiSeries | null) {
    this._raw.set(value ?? []);
    this.updateColorScheme(value);
    this.buildAxisTicks();
  }

  xMax = 1;
  xTickFormat = (v: number) => String(v);

  tooltipVisible = false;
  tooltipText = '';
  tooltipX = 0;
  tooltipY = 0;
  lastActivateEvent: any = null;
  currentMouseX = 0;
  currentMouseY = 0;

  onActivate(event: any) {
    this.lastActivateEvent = event;

    const value = event.value?.value ?? 0;

    // Total de la barra completa
    const barName = event.value?.series;
    const bar = this.normalized().find(b => b.name === barName);
    const total = bar?.series?.reduce((sum: number, s: any) => sum + (+s.value || 0), 0) ?? 0;

    this.tooltipText = `${value} de ${total}`;

    // Posición inicial del tooltip
    this.tooltipX = this.currentMouseX - 30;
    this.tooltipY = this.currentMouseY - 35;

    this.tooltipVisible = true;
    this.cdr.detectChanges();
  }

  onDeactivate() {
    this.tooltipVisible = false;
    this.lastActivateEvent = null;
  }


  onChartMouseMove(event: MouseEvent) {
    this.currentMouseX = event.clientX;
    this.currentMouseY = event.clientY;
    if (this.lastActivateEvent) {
      this.tooltipX = this.currentMouseX - 30;
      this.tooltipY = this.currentMouseY - 35;
      this.cdr.detectChanges();
    }
  }


  hideTooltip() {
    this.tooltipVisible = false;
  }

  // ---- Ciclo de cambios ----
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['view']) this.computedView = this.view;

    if (changes['customColors'] && this.customColors.length > 0) {
      this.colorScheme = {
        name: 'custom-legend',
        selectable: true,
        group: ScaleType.Ordinal,
        domain: this.customColors.map((c) => c.value),
      };
    }
    this.buildAxisTicks();
  }

  private buildAxisTicks(): void {
    const data = this.normalized() ?? [];
    let rawMax = 0;
    for (const g of data) {
      const total = (g?.series ?? []).reduce((acc: number, s: any) => acc + (+s?.value || 0), 0);
      if (total > rawMax) rawMax = total;
    }

    const ceilMax = Math.max(1, Math.ceil(rawMax));

    // Si el máximo es pequeño, mostramos todos los ticks para no perder detalle.
    if (ceilMax <= 15) {
      const niceMax = ceilMax % 2 === 0 ? ceilMax : ceilMax + 1;
      this.xTicks = Array.from({length: niceMax + 1}, (_, i) => i);
      this.xMax = niceMax;
      return;
    }

    // Para números más grandes, calculamos un "step" para no tener más de 5-7 ticks.
    const desiredTicks = 5;
    const roughStep = ceilMax / desiredTicks;

    // Buscamos un "step" agradable (múltiplo de 5, 10, 20, etc.)
    const magnitudes = [1, 2, 5, 10, 20, 25, 50, 100, 200, 500, 1000, 2000, 5000];
    const step = magnitudes.find(m => m > roughStep) ?? Math.ceil(roughStep / 10) * 10;

    const niceMax = Math.ceil(ceilMax / step) * step;

    const ticks = [];
    for (let i = 0; i <= niceMax; i += step) {
      ticks.push(i);
    }

    this.xTicks = ticks;
    this.xMax = niceMax;
  }

  private updateColorScheme(data: MultiSeries | null): void {
    if (this.customColors.length > 0) return;

    if (!data || data.length === 0) {
      this.colorScheme = 'vivid';
      return;
    }

    const seriesNames = new Set<string>();
    for (const item of data) {
      for (const s of item.series) {
        // Usar el nombre original para los colores, no el identificador único
        const originalName = (s as any).originalName || s.name;
        seriesNames.add(originalName);
      }
    }

    const domain = Array.from(seriesNames).map(
      (name) => RISK_COLOR[name as Risk] || '#999'
    );

    this.colorScheme = {
      name: 'risk-colors',
      selectable: true,
      group: ScaleType.Ordinal,
      domain,
    };
  }
}
